import { useState, useCallback } from 'react';
import {
  search,
  searchByReference,
  isAlgoliaConfigured,
} from '../services/algolia';
import type { WatchReferenceAlgoliaHit } from '../types';

interface UseAlgoliaSearchState {
  results: WatchReferenceAlgoliaHit[];
  totalHits: number;
  loading: boolean;
  error: string | null;
  query: string;
}

export function useAlgoliaSearch() {
  const [state, setState] = useState<UseAlgoliaSearchState>({
    results: [],
    totalHits: 0,
    loading: false,
    error: null,
    query: '',
  });

  const performSearch = useCallback(
    async (
      query: string,
      options?: { brandId?: string; limit?: number; exactMatch?: boolean },
    ) => {
      if (!query.trim()) {
        setState({
          results: [],
          totalHits: 0,
          loading: false,
          error: null,
          query: '',
        });
        return;
      }

      if (!isAlgoliaConfigured()) {
        setState((prev) => ({
          ...prev,
          error: 'Algolia is not configured',
          loading: false,
        }));
        return;
      }

      setState((prev) => ({ ...prev, loading: true, error: null, query }));

      try {
        const response = await search({
          query,
          brandId: options?.brandId,
          limit: options?.limit || 20,
          exactMatch: options?.exactMatch,
        });

        setState({
          results: response.hits,
          totalHits: response.nbHits,
          loading: false,
          error: null,
          query,
        });
      } catch (err) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: err instanceof Error ? err.message : 'Search failed',
        }));
      }
    },
    [],
  );

  const searchReference = useCallback(
    async (reference: string, brandId?: string) => {
      if (!reference.trim()) return;

      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const response = await searchByReference({
          query: reference,
          brandId,
          limit: 10,
        });

        setState({
          results: response.hits,
          totalHits: response.nbHits,
          loading: false,
          error: null,
          query: reference,
        });
      } catch (err) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: err instanceof Error ? err.message : 'Search failed',
        }));
      }
    },
    [],
  );

  const clear = useCallback(() => {
    setState({
      results: [],
      totalHits: 0,
      loading: false,
      error: null,
      query: '',
    });
  }, []);

  return {
    ...state,
    search: performSearch,
    searchReference,
    clear,
    isConfigured: isAlgoliaConfigured(),
  };
}
